import {
  VISIT_TYPE,
  buildReferenceVisitOptions,
  calcLegacyPeriodDuration,
  calcScreeningDuration,
  createVisitDraft,
  formatApiNumericField,
  hasScreeningWindowFields,
  isAdhocVisit,
  normalizeVisitDrafts,
  pickScreeningFieldsFromRow,
  visitsForVisitRail,
  visitsFromApiRows,
} from "./scheduleVisitDraft";

export { calcScreeningDuration };

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

function isScreeningDraft(visit) {
  return !!(visit?.visitType === VISIT_TYPE.SCREENING || visit?.isScreening);
}

function resolveScreeningDuration(screeningWindowStart, screeningWindowFinish, apiDuration, draftDuration) {
  const start = formatApiNumericField(screeningWindowStart);
  const finish = formatApiNumericField(screeningWindowFinish);
  if (start !== "" && finish !== "") {
    return calcScreeningDuration(start, finish);
  }
  const fromApi = formatApiNumericField(apiDuration);
  if (fromApi !== "") return fromApi;
  const fromDraft = formatApiNumericField(draftDuration);
  if (fromDraft !== "") return fromDraft;
  return "";
}

/** GET cohort may omit ScreeningWindow* — patch raw API rows from saved drafts before merge. */
export function patchApiVisitRowsWithScreeningDrafts(apiRows = [], userDrafts = []) {
  const rows = Array.isArray(apiRows) ? apiRows : [];
  if (!rows.length) return rows;

  const draftByDbId = new Map();
  visitsForVisitRail(userDrafts).forEach((draft) => {
    if (draft.visitsPerStudyId && isScreeningDraft(draft)) {
      draftByDbId.set(String(draft.visitsPerStudyId), draft);
    }
  });
  if (!draftByDbId.size) return rows;

  return rows.map((row) => {
    const visitId = pick(row, "VisitsPerStudyId", "visitsPerStudyId");
    if (!visitId) return row;
    const draft = draftByDbId.get(String(visitId));
    if (!draft) return row;

    const fromApi = pickScreeningFieldsFromRow(row);
    const needsPatch =
      !hasScreeningWindowFields(row) &&
      (draft.screeningWindowStart !== "" || draft.screeningWindowFinish !== "");
    if (!needsPatch) return row;

    return {
      ...row,
      ScreeningWindowStart:
        fromApi.screeningWindowStart !== ""
          ? pick(row, "ScreeningWindowStart", "screeningWindowStart", "ScreeningStart")
          : draft.screeningWindowStart,
      ScreeningWindowFinish:
        fromApi.screeningWindowFinish !== ""
          ? pick(row, "ScreeningWindowFinish", "screeningWindowFinish", "ScreeningEnd")
          : draft.screeningWindowFinish,
      ScreeningDuration:
        fromApi.screeningDuration !== ""
          ? pick(row, "ScreeningDuration", "screeningDuration")
          : draft.screeningDuration ||
            calcScreeningDuration(draft.screeningWindowStart, draft.screeningWindowFinish),
    };
  });
}

function parseNum(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function isBlank(value) {
  return String(value ?? "").trim() === "";
}

const SCREENING_FINISH_ALLOWED = new Set([-1, 0, 1]);

export function getScreeningRangeError(start, finish, { requireBoth = false, compareOnly = false } = {}) {
  const startBlank = isBlank(start);
  const finishBlank = isBlank(finish);
  if (requireBoth && (startBlank || finishBlank)) {
    return "Enter screening window start and finish.";
  }
  if (startBlank || finishBlank) return null;

  const startNum = parseNum(start);
  const finishNum = parseNum(finish);
  if (!Number.isFinite(startNum) || !Number.isFinite(finishNum)) {
    return "Screening window values must be numbers.";
  }
  if (startNum > finishNum) {
    return "Screening window finish must be equal or greater than start.";
  }
  if (compareOnly) return null;

  if (startNum >= 1) {
    return "Screening start should be negative or zero.";
  }
  if (!SCREENING_FINISH_ALLOWED.has(finishNum)) {
    return "Screening finish should be -1, 0, or 1.";
  }
  return null;
}

export function getResidentialRangeError(start, finish, { requireBoth = false } = {}) {
  const startBlank = isBlank(start);
  const finishBlank = isBlank(finish);
  if (requireBoth && (startBlank || finishBlank)) {
    return "Enter start and finish period.";
  }
  if (startBlank || finishBlank) return null;

  const startNum = parseNum(start);
  const finishNum = parseNum(finish);
  if (!Number.isFinite(startNum) || !Number.isFinite(finishNum)) {
    return "Period values must be numbers.";
  }
  if (startNum > finishNum) {
    return "Finish period must be equal or greater than start.";
  }
  return null;
}

export function validateScreeningFields(visit, visitLabel = "Screening") {
  const start = parseNum(visit.screeningWindowStart);
  const finish = parseNum(visit.screeningWindowFinish);
  const duration = parseNum(visit.screeningDuration);

  const rangeError = getScreeningRangeError(
    visit.screeningWindowStart,
    visit.screeningWindowFinish,
    { requireBoth: true },
  );
  if (rangeError) {
    return { valid: false, message: `${visitLabel}: ${rangeError}` };
  }
  if (!Number.isFinite(duration)) {
    return { valid: false, message: `${visitLabel}: screening duration is invalid.` };
  }
  return { valid: true };
}

export function validateResidentialFields(visit, visitLabel) {
  const duration = parseNum(visit.periodDuration);

  const rangeError = getResidentialRangeError(visit.startPeriod, visit.finishPeriod, {
    requireBoth: true,
  });
  if (rangeError) {
    return { valid: false, message: `${visitLabel}: ${rangeError}` };
  }
  if (!Number.isFinite(duration)) {
    return { valid: false, message: `${visitLabel}: period duration is invalid.` };
  }
  return { valid: true };
}

export function validateNonResidentialFields(visit, visitLabel) {
  const occur = parseNum(visit.nonResidVisitOccur);
  if (isBlank(visit.nonResidVisitOccur)) {
    return { valid: false, message: `${visitLabel}: enter visit time point (day).` };
  }
  if (!Number.isFinite(occur) || occur < -1) {
    return {
      valid: false,
      message: `${visitLabel}: visit time point must be -1 or greater.`,
    };
  }
  return { valid: true };
}

export function validateVisitDraftDetails(visit, { screening = false } = {}) {
  const label = visit.visitName || "Visit";
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) {
    if (!screening) return { valid: true };
    return validateScreeningFields(visit, label);
  }
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) {
    return validateResidentialFields(visit, label);
  }
  return validateNonResidentialFields(visit, label);
}

export function validateAllVisitDrafts(visits = [], { screening = false } = {}) {
  const list = normalizeVisitDrafts(visits, { screening }).filter((v) => !isAdhocVisit(v));
  for (const visit of list) {
    const name = String(visit.visitName || "").trim().toLowerCase();
    if (!visit.locked && name === "screening") {
      return { valid: false, message: "Visit name cannot be Screening." };
    }
    const detailCheck = validateVisitDraftDetails(visit, { screening });
    if (!detailCheck.valid) return detailCheck;
  }
  const names = list
    .filter((v) => !v.locked && v.visitType !== VISIT_TYPE.SCREENING)
    .map((v) => String(v.visitName || "").trim().toLowerCase())
    .filter(Boolean);
  if (new Set(names).size !== names.length) {
    return { valid: false, message: "Duplicate visit names are not allowed." };
  }
  return { valid: true, value: list };
}

function coalesceVisitDetailValue(apiValue, draftValue) {
  const fromApi = formatApiNumericField(apiValue);
  if (fromApi !== "") return fromApi;
  return formatApiNumericField(draftValue);
}

function mergePersistedVisitDetails(api, draft) {
  const screeningWindowStart = coalesceVisitDetailValue(
    api.screeningWindowStart,
    draft.screeningWindowStart,
  );
  const screeningWindowFinish = coalesceVisitDetailValue(
    api.screeningWindowFinish,
    draft.screeningWindowFinish,
  );
  const screeningDuration = resolveScreeningDuration(
    screeningWindowStart,
    screeningWindowFinish,
    api.screeningDuration,
    draft.screeningDuration,
  );

  return {
    screeningWindowStart,
    screeningWindowFinish,
    screeningDuration,
    startPeriod: coalesceVisitDetailValue(api.startPeriod, draft.startPeriod),
    finishPeriod: coalesceVisitDetailValue(api.finishPeriod, draft.finishPeriod),
    periodDuration:
      coalesceVisitDetailValue(api.periodDuration, draft.periodDuration) ||
      calcLegacyPeriodDuration(
        coalesceVisitDetailValue(api.startPeriod, draft.startPeriod),
        coalesceVisitDetailValue(api.finishPeriod, draft.finishPeriod),
      ),
    tolerencePeriod: coalesceVisitDetailValue(api.tolerencePeriod, draft.tolerencePeriod),
    upperTolerenceLimit: coalesceVisitDetailValue(
      api.upperTolerenceLimit,
      draft.upperTolerenceLimit,
    ),
    nonResidVisitOccur: coalesceVisitDetailValue(
      api.nonResidVisitOccur,
      draft.nonResidVisitOccur,
    ),
    nonResidTolerencePeriod: coalesceVisitDetailValue(
      api.nonResidTolerencePeriod,
      draft.nonResidTolerencePeriod,
    ),
    nonResidUpperTolerenceLimit: coalesceVisitDetailValue(
      api.nonResidUpperTolerenceLimit,
      draft.nonResidUpperTolerenceLimit,
    ),
    nonResidVisitTypeId: draft.nonResidVisitTypeId || api.nonResidVisitTypeId || "",
    refVisitId: draft.refVisitId || api.refVisitId || "",
    plannerSnapshot: draft.plannerSnapshot || api.plannerSnapshot || null,
  };
}

function findMatchingUserDraft(draftByDbId, apiDraft) {
  if (!apiDraft) return null;
  if (apiDraft.visitsPerStudyId) {
    const byDbId = draftByDbId.get(String(apiDraft.visitsPerStudyId));
    if (byDbId) return byDbId;
  }
  if (apiDraft.id) {
    for (const draft of draftByDbId.values()) {
      if (String(draft.id) === String(apiDraft.id)) return draft;
    }
  }
  return null;
}

export function mergeDraftsWithApiVisits(userDrafts = [], apiRows = [], { screening = false } = {}) {
  const userList = visitsForVisitRail(userDrafts);
  const patchedRows = patchApiVisitRowsWithScreeningDrafts(apiRows, userDrafts);
  const apiDrafts = visitsFromApiRows(patchedRows, { excludeSpecial: true });

  if (!apiDrafts.length) {
    return userList;
  }

  const draftByDbId = new Map();
  userList.forEach((draft) => {
    if (draft.visitsPerStudyId) {
      draftByDbId.set(String(draft.visitsPerStudyId), draft);
    }
  });

  const merged = apiDrafts.map((api) => {
    const draft = findMatchingUserDraft(draftByDbId, api);
    if (!draft) {
      return createVisitDraft(api);
    }
    const details = mergePersistedVisitDetails(api, draft);
    const isScreeningVisit =
      api.isScreening ||
      draft.isScreening ||
      api.visitType === VISIT_TYPE.SCREENING ||
      draft.visitType === VISIT_TYPE.SCREENING;
    return createVisitDraft({
      ...draft,
      ...api,
      ...details,
      visitName: api.visitName || draft.visitName,
      visitType: isScreeningVisit
        ? VISIT_TYPE.SCREENING
        : api.visitType || draft.visitType,
      visitsPerStudyId: api.visitsPerStudyId || draft.visitsPerStudyId,
      id: api.id || draft.id,
      isScreening: isScreeningVisit,
    });
  });

  const mergedDbIds = new Set(
    merged.map((visit) => String(visit.visitsPerStudyId || "")).filter(Boolean),
  );
  const orphanDrafts = userList.filter(
    (draft) =>
      draft.visitsPerStudyId && !mergedDbIds.has(String(draft.visitsPerStudyId)),
  );

  return visitsForVisitRail([...merged, ...orphanDrafts]);
}

export function buildScreeningDetailsPayload({
  studyScheduleId,
  visit,
  organizationId,
  createdBy,
}) {
  return {
    StudyId: studyScheduleId,
    VisitId: visit.visitsPerStudyId,
    ScreeningStart: Number(visit.screeningWindowStart),
    ScreeningEnd: Number(visit.screeningWindowFinish),
    ScreeningDuration: Number(visit.screeningDuration),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "ScreeningPerStudy",
  };
}

function formatVisitNumericField(value) {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "";
}

function formatVisitNullableInt(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : null;
}

function inferVisitOrder(visit) {
  const fromField = Number(visit?.visitOrder);
  if (Number.isFinite(fromField)) return fromField;
  const name = String(visit?.visitName || "").trim();
  const match = name.match(/(\d+)\s*$/);
  return match ? Number(match[1]) : null;
}

export function resolveRefVisitIdForSave(visit, allVisits = []) {
  const existing = String(visit?.refVisitId || "").trim();
  if (existing) return existing;

  const visitNum = inferVisitOrder(visit);
  if (Number.isFinite(visitNum) && visitNum > 1) {
    const priorByOrder = [...visitsForVisitRail(allVisits)]
      .filter((row) => !isAdhocVisit(row))
      .find((row) => inferVisitOrder(row) === visitNum - 1);
    if (priorByOrder?.visitsPerStudyId) {
      return String(priorByOrder.visitsPerStudyId);
    }
  }

  const ordered = [...visitsForVisitRail(allVisits)]
    .filter((row) => !isAdhocVisit(row))
    .sort((a, b) => {
      const av = Number(a.visitOrder ?? 999);
      const bv = Number(b.visitOrder ?? 999);
      if (Number.isFinite(av) && Number.isFinite(bv) && av !== bv) return av - bv;
      return String(a.visitName || "").localeCompare(String(b.visitName || ""));
    });

  const idx = ordered.findIndex(
    (row) =>
      String(row.visitsPerStudyId) === String(visit?.visitsPerStudyId) ||
      row.id === visit?.id,
  );
  if (idx <= 0) return "";

  const prior = ordered[idx - 1];
  return prior?.visitsPerStudyId ? String(prior.visitsPerStudyId) : "";
}

export function validateVisitReferenceForSave(visit, allVisits = []) {
  const { show, options } = buildReferenceVisitOptions(allVisits, visit);
  if (!show) {
    return { valid: true };
  }

  const refVisitId = String(visit?.refVisitId || "").trim();
  if (refVisitId && options.some((option) => option.value === refVisitId)) {
    return { valid: true };
  }

  const visitLabel = visit?.visitName || "Visit";
  const visitNum = inferVisitOrder(visit);
  if (Number.isFinite(visitNum) && visitNum > 1) {
    return {
      valid: false,
      message: `${visitLabel}: select reference visit, or save Visit ${visitNum - 1} first.`,
    };
  }
  return {
    valid: false,
    message: `${visitLabel}: select reference visit.`,
  };
}

export function buildIndividualVisitPayload({
  studyScheduleId,
  visit,
  organizationId,
  createdBy,
  action = "InsertVisitTypeDetails",
  visits = [],
}) {
  const isResidential = visit.visitType === VISIT_TYPE.RESIDENTIAL;
  const periodDuration =
    visit.periodDuration ||
    calcLegacyPeriodDuration(visit.startPeriod, visit.finishPeriod);
  const refVisitId = resolveRefVisitIdForSave(visit, visits);

  return {
    StudyId: studyScheduleId,
    VisitId: visit.visitsPerStudyId,
    VisitName: visit.visitName,
    TypeOfVisit: isResidential ? "Residential" : "Non residential",
    ResidentialVisitStart: isResidential
      ? formatVisitNullableInt(visit.startPeriod)
      : null,
    ResidentialVisitEnd: isResidential
      ? formatVisitNullableInt(visit.finishPeriod)
      : null,
    ResidentialVisitDuration: isResidential
      ? formatVisitNullableInt(periodDuration)
      : null,
    ResidentialVisitTolerence: isResidential
      ? formatVisitNullableInt(visit.tolerencePeriod)
      : null,
    NonResidentialVisitTypeId: isResidential
      ? null
      : String(visit.nonResidVisitTypeId ?? "").trim() || null,
    NonResidentialVisitOccure: isResidential
      ? null
      : formatVisitNullableInt(visit.nonResidVisitOccur),
    NonResiVisitTolerence: isResidential
      ? null
      : formatVisitNullableInt(visit.nonResidTolerencePeriod),
    UpperTolerenceLimit: isResidential
      ? formatVisitNullableInt(visit.upperTolerenceLimit)
      : formatVisitNullableInt(visit.nonResidUpperTolerenceLimit),
    RefVisitId: refVisitId || null,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: action,
  };
}

export function visitHasPersistableDetails(visit, { screening = false } = {}) {
  if (!visit?.visitsPerStudyId) return false;
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) {
    return (
      screening &&
      !isBlank(visit.screeningWindowStart) &&
      !isBlank(visit.screeningWindowFinish)
    );
  }
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) {
    return !isBlank(visit.startPeriod) && !isBlank(visit.finishPeriod);
  }
  return !isBlank(visit.nonResidVisitOccur);
}

export function filterApiVisitsForGroup(apiRows = [], groupNameId = null) {
  const rows = Array.isArray(apiRows) ? apiRows : [];
  if (!groupNameId) return rows;
  return rows.filter(
    (row) =>
      pick(row, "GroupNameIdRef", "groupNameIdRef", "GroupNameId", "groupNameId") ===
      groupNameId,
  );
}
